import { addLeadingSlash } from 'core/common/utils/string';
import { GrowthBookFeatureFlagsBuilder } from 'core/feature-flags/builders';
import { FunnelServiceWithCachingBuilder } from 'core/funnel/builders/FunnelServiceWithCachingBuilder';
import { StaticProps } from 'core/funnel/entities';
import { FunnelConfigUnavailableError } from 'core/funnel/errors';
import { Funnel } from 'core/funnel/pages';
import { config } from 'src/config';

export const getStaticProps = async (context: StaticProps) => {
  const funnelServiceWithCachingBuilder = new FunnelServiceWithCachingBuilder(config);
  const growthBookFeatureFlagsBuilder = new GrowthBookFeatureFlagsBuilder(config);

  if (config.isBuildPhase) {
    funnelServiceWithCachingBuilder.addCaching();
    growthBookFeatureFlagsBuilder.addCaching();
  } else if (!config.isDevelopment) {
    await funnelServiceWithCachingBuilder.addRedisCaching();
    growthBookFeatureFlagsBuilder.addSkipFeaturesCache();
  }

  const funnelService = funnelServiceWithCachingBuilder.getResult();
  const growthBookFeatureFlags = growthBookFeatureFlagsBuilder.getResult();

  const [funnels, features] = await Promise.all([
    funnelService.getFunnels({
      funnelLink: addLeadingSlash(context.params.funnel),
      // language: context.locale,
    }),
    growthBookFeatureFlags.loadFeatures(),
  ]);

  const featuresPayload = features ? growthBookFeatureFlags.getPayload() : null;

  growthBookFeatureFlags.destroy();

  if (!funnels) {
    throw new FunnelConfigUnavailableError(context.params.funnel);
  }

  if (!funnels.length) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  const currentFunnel = funnels[0];

  const analytics = currentFunnel?.analytics.length ? currentFunnel.analytics : [];

  return {
    props: {
      // Unknown page id is used below to redirect user to initial screen of active flow
      pageId: 'unknown',
      funnel: context.params.funnel,
      featuresPayload,
      funnelConfig: currentFunnel,
      analytics,
      currentFunnelName: currentFunnel?.name ?? '',
    },
  };
};

export async function getStaticPaths() {
  const funnelService = new FunnelServiceWithCachingBuilder(config).getResult();

  const funnels = await funnelService.getFunnels();

  if (!funnels || !funnels.length) {
    return { paths: [], fallback: 'blocking' };
  }

  const paths = funnelService.getFunnelsPaths(funnels);

  return { paths, fallback: 'blocking' };
}

export default Funnel;
